import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

import { TranslateService } from '@ngx-translate/core';
import { ApiService } from './services/api.service';
import { CurrentWhiteLabelApplication } from './utils/current-white-label-application';
import { NetworkService } from './services/network.service';
import { FileService } from './services/file.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Settings } from 'luxon';
import { BackButtonService } from './services/back-button.service';
import { PwaService } from './services/pwa.service';
import { KeyboardService } from './services/keyboard.service';
import { WhiteLabelApplicationService } from './services/white-label-application.service';
import { TranslationService } from './services/translation.service';
import { SettingsService } from './services/settings.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { DeepLinkService } from './services/deep-link.service';
import { PushService } from './services/push.service';
import { AnalyticsService } from './services/analytics.service';
import { Device } from '@capacitor/device';
import { AnalyticsType } from './enums/analytics-type';
import { environment } from 'src/environments/environment';
import { LoaderComponent } from './components/loader/loader.component';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { StorageService } from './services/storage.service';
import { GlobalErrorHandler } from './utils/gloabl-error-handler';
import { DeviceService } from './services/device.service';
import { BadgeService } from './services/badge.service';
import { DeviceRedirectService } from './services/device-redirect.service';
import { ProjectService } from './services/project.service';
import { NotificationService } from './services/notification.service';
import { DesktopHeaderComponent } from './components/desktop/header/desktop-header.component';
import { PermissionService } from './services/permission.service';
import { PermissionType } from './enums/permission-type';
import { SafeAreaService } from './services/safe-area.service';
import { ProjectStore } from './pages/project/project.store';
import { PreviewService } from './services/preview.service';
import { addIcons } from 'ionicons';
import { LocationService } from './services/location.service';
import {
  arrowBack,
  arrowForward,
  chevronDown,
  chevronUp,
  close,
  attach,
  send,
  ellipsisVertical,
} from 'ionicons/icons';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [LoaderComponent, IonApp, IonRouterOutlet, DesktopHeaderComponent],
})
export class AppComponent implements OnDestroy {
  public ready = false;
  public checked = false;
  public isDesktop: boolean;

  private readonly HAS_OPENED_KEY = 'hasOpenedApp';
  private performanceStartTime: number = 0;

  private readonly projectStore = inject(ProjectStore);
  private readonly platformId = inject(PLATFORM_ID);
  project = computed(() => this.projectStore.project());

  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly apiService = inject(ApiService);
  private readonly currentApp = inject(CurrentWhiteLabelApplication);
  private readonly networkService = inject(NetworkService);
  private readonly fileService = inject(FileService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly backButtonService = inject(BackButtonService);
  private readonly pwaService = inject(PwaService);
  private readonly whiteLabelApplicationService = inject(
    WhiteLabelApplicationService
  );
  private readonly cd = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly keyboardService = inject(KeyboardService);
  private readonly translationService = inject(TranslationService);
  private readonly settingsService = inject(SettingsService);
  private readonly pushService = inject(PushService);
  private readonly previewService = inject(PreviewService);
  private readonly deeplinkService = inject(DeepLinkService);
  private readonly storageService = inject(StorageService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly deviceService = inject(DeviceService);
  private readonly deviceRedirectService = inject(DeviceRedirectService);
  private readonly renderer = inject(Renderer2);
  private readonly projectService = inject(ProjectService);
  private readonly notificationService = inject(NotificationService);
  private readonly safeAreaService = inject(SafeAreaService);
  private readonly permissionService = inject(PermissionService);
  private readonly badgeService = inject(BadgeService);
  private readonly locationService = inject(LocationService);
  private readonly document = inject(DOCUMENT);

  constructor() {
    addIcons({
      close,
      arrowForward,
      arrowBack,
      chevronDown,
      chevronUp,
      attach,
      send,
      ellipsisVertical,
    });

    if (!isPlatformBrowser(this.platformId)) {
      this.isDesktop = false;
      this.initializeAppServer();
      return;
    }

    this.performanceStartTime = performance.now();
    this.initializeApp();
    this.apiService.userLoggedIn.subscribe(() =>
      this.projectService.getStarredProjects()
    );
    this.apiService.userLoggedOut.subscribe(() => {
      SplashScreen.show();
      this.ready = false;
      this.initializeApp();

      if (this.currentApp.isPrivate()) {
        this.router.navigateByUrl('/login', { replaceUrl: true });
      }
    });

    this.isDesktop = this.pwaService.isDesktop();
  }

  async initializeApp() {
    await this.whiteLabelApplicationService.check();
    await this.setupLocalization();
    await this.apiService.init();

    this.deeplinkService.checkDeepLinks();
    this.addLinkListener();

    await this.deviceRedirectService.checkAppStoreRedirect();
    if (!(await this.storageService.get(this.HAS_OPENED_KEY))) {
      await this.deviceRedirectService.checkRedirect();
    }

    if (this.pwaService.isDesktop()) {
      document.documentElement.classList.add('app-desktop');
    }

    await this.settingsService.checkAppearance();
    await this.settingsService.checkContrast();

    this.ready = true;
    this.cd.detectChanges();
    SplashScreen.hide().then(() => {
      this.analyticsService.logPerformance(
        AnalyticsType.APP_LOADED,
        this.performanceStartTime
      );
    });

    this.clearBadge();
    this.addResumeListener();
    this.deviceService.syncDeviceInfoForLanguageChange().catch(() => {});

    this.setPageTitle();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) this.setPageTitle();
    });

    if (this.pwaService.isPwa()) {
      this.allowZoom();
      this.analyticsService.removeUtmSourceFromUrl(
        new URL(window.location.href)
      );

      // Temporary to fix devices for non bouwapp PWAs that are linked to wrong PWA. BA-1774
      if (window.location.hostname.split(':')[0] !== 'debouw.app') {
        this.deviceService.syncWhiteLabelApplication();
      }
    } else {
      this.analyticsService.logEvent(AnalyticsType.APP_STARTUP, {});
    }

    this.projectService.checkStarredProjects();

    const language = await Device.getLanguageCode();
    this.analyticsService.logEvent(AnalyticsType.DEVICE_LANGUAGE, {
      language: language.value,
    });

    this.pushService.setupFirebaseListeners();

    this.fileService.removeScheduledFiles();

    this.networkService.initialize();

    this.translationService.setTranslationTitles();
    this.setupDevice();
    this.safeAreaService.initialize();

    this.refreshNotificationCount();

    this.backButtonService.attach();

    if (false === this.notificationService.isOpenedTroughNotification) {
      if (
        await this.permissionService.isPermissionEnabled(PermissionType.PUSH)
      ) {
        this.pushService.enablePush();
      } else {
        this.pushService.disablePush();
      }
    }

    this.storageService.remove(GlobalErrorHandler.RELOAD_COUNT_KEY);
    this.storageService.remove(GlobalErrorHandler.LAST_RELOAD_TIME_KEY);

    if (!(await this.storageService.get(this.HAS_OPENED_KEY))) {
      await this.storageService.set(this.HAS_OPENED_KEY, true);
    }

    void this.locationService.getCurrentPositionSafe();
  }

  private async initializeAppServer() {
    await this.whiteLabelApplicationService.check();
    await this.apiService.init();
    const lang = this.translationService.getAppLanguage();
    this.translate.setDefaultLang(TranslationService.DEFAULT_LANG);
    await firstValueFrom(this.translate.use(lang));
    this.document.documentElement.lang = lang;
    this.ready = true;
  }

  allowZoom() {
    const metaTag = this.document.querySelector('meta[name="viewport"]');
    const content =
      'minimum-scale=1.0, maximum-scale=5.0, initial-scale=1.0, user-scalable=yes, viewport-fit=cover, width=device-width';
    this.renderer.setAttribute(metaTag, 'content', content);
  }

  setupDevice() {
    this.keyboardService.hideAccessoryBar();
  }

  async setupLocalization() {
    this.translate.setDefaultLang(TranslationService.DEFAULT_LANG);
    const language = this.translationService.getAppLanguage();
    Settings.defaultLocale = language;
    await firstValueFrom(this.translate.use(language));
    this.document.documentElement.lang = language;
  }

  async setPageTitle() {
    let route = this.route;
    while (route.firstChild) {
      route = route.firstChild;
    }
    let pageTitle = this.currentApp.getName();
    let projectName;
    const currentSlug = route.snapshot.paramMap.get('slug');
    if (currentSlug && this.router.url.startsWith('/projects')) {
      if (this.projectService.isProjectLoading()) {
        await this.projectService.waitUntilProjectLoaded();
      }
      projectName = this.project()?.name;
    }
    const currentTitle = route.snapshot.data.title;
    if (projectName) pageTitle += ' - ' + projectName;
    if (currentTitle) pageTitle += ' - ' + this.translate.instant(currentTitle);
    this.title.setTitle(pageTitle);
  }

  ngOnDestroy() {
    this.backButtonService.detach();
  }

  private addLinkListener() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        const url = await this.analyticsService.removeUtmSourceFromUrl(
          new URL(event.url)
        );
        if (
          event.url.startsWith(environment.linkPrefix + '://') ||
          event.url.startsWith(DeepLinkService.PUSH_LINK_PREFIX + '://')
        ) {
          this.router.navigate(['deeplink', 'wrapper'], {
            queryParams: { link: event.url },
          });
        } else if (event.url.startsWith('https://')) {
          this.previewService.checkPreviewToken(url);
          this.router.navigateByUrl(url.pathname + url.search);
        }
      });
    });
  }

  private addResumeListener() {
    App.addListener('resume', () => {
      this.zone.run(() => {
        this.settingsService.checkAppearance();
        this.settingsService.checkContrast();
        this.deviceService.syncDeviceInfoForLanguageChange().catch(() => {});
        this.clearBadge();
        this.refreshNotificationCount();
      });
    });
  }

  private clearBadge() {
    this.badgeService.clearBadge();
  }

  private refreshNotificationCount(): void {
    this.deviceService
      .getData()
      .then((deviceData) => {
        this.notificationService.setUnreadCount(
          deviceData.notificationUnreadCount ?? 0
        );
      })
      .catch(() => {});
  }
}
